import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Alert,
  Checkbox,
  ListItemText
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import ParamInput from '../shared/ParamInput';
import InfoPopover from '../shared/InfoPopover';
import { useSymbols } from '../../../provider/symbolProvider';

const IndicatorStep = ({ components, formData, updateFormData, errors }) => {
  const { symbolNames } = useSymbols();

  const handleSymbolsChange = (event) => {
    const value = event.target.value;
    updateFormData({ selectedSymbols: typeof value === 'string' ? value.split(',') : value });
  };
  // Add indicator to selection
  const handleAddIndicator = (componentId) => {
    const component = components.find(c => c.componentId === componentId);
    if (!component) return;

    // Check if already added
    if (formData.indicators.some(ind => ind.componentId === componentId)) {
      return;
    }

    // Initialize with default values
    const defaultValues = {};
    component.parameters.forEach(param => {
      if (param.defaultValue !== undefined && param.defaultValue !== null) {
        defaultValues[param.name] = param.defaultValue;
      }
    });

    updateFormData({
      indicators: [
        ...formData.indicators,
        {
          componentId,
          component,
          parameters: component.parameters,
          values: defaultValues
        }
      ]
    });
  };

  // Remove indicator
  const handleRemoveIndicator = (componentId) => {
    updateFormData({
      indicators: formData.indicators.filter(ind => ind.componentId !== componentId)
    });
  };

  // Update indicator parameter value
  const handleParamChange = (componentId, paramName, value) => {
    updateFormData({
      indicators: formData.indicators.map(ind => {
        if (ind.componentId === componentId) {
          return {
            ...ind,
            values: { ...ind.values, [paramName]: value }
          };
        }
        return ind;
      })
    });
  };

  return (
    <Box>
      {/* Symbols Selection */}
      <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
        Select Symbols
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.6)' }}>
        Choose the symbols your strategy will trade on.
      </Typography>

      {errors.selectedSymbols && (
        <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
          {errors.selectedSymbols}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 4 }}>
        <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Symbols</InputLabel>
        <Select
          multiple
          value={formData.selectedSymbols || []}
          onChange={handleSymbolsChange}
          label="Symbols"
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size="small" sx={{ backgroundColor: '#27B56D', color: '#fff' }} />
              ))}
            </Box>
          )}
          sx={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            color: '#fff',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#27B56D' }
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: '#1e1e2e',
                border: '1px solid rgba(255,255,255,0.1)',
                maxHeight: 300,
                '& .MuiMenuItem-root': {
                  color: '#fff',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                  '&.Mui-selected': { backgroundColor: 'rgba(39, 181, 109, 0.3)' }
                }
              }
            }
          }}
        >
          {symbolNames.map((symbol) => (
            <MenuItem key={symbol} value={symbol}>
              <Checkbox checked={formData.selectedSymbols?.includes(symbol)} sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#27B56D' } }} />
              <ListItemText primary={symbol} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Indicators Section */}
      <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
        Select Indicators
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.6)' }}>
        Choose one or more indicators for your strategy.
      </Typography>

      {errors.indicators && (
        <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
          {errors.indicators}
        </Alert>
      )}

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Add Indicator</InputLabel>
        <Select
          value=""
          onChange={(e) => handleAddIndicator(e.target.value)}
          label="Add Indicator"
          sx={{
            backgroundColor: 'rgba(255,255,255,0.05)',
            color: '#fff',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563eb' }
          }}
        >
          {components.map(comp => (
            <MenuItem
              key={comp.componentId}
              value={comp.componentId}
              disabled={formData.indicators.some(ind => ind.componentId === comp.componentId)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography>{comp.name}</Typography>
                {comp.tooltip && (
                  <Box sx={{ ml: 'auto' }}>
                    <InfoPopover
                      title={comp.name}
                      description={comp.tooltip}
                      tags={comp.tags}
                      accentColor="#2563eb"
                      iconSx={{ color: 'rgba(0,0,0,0.4)', '&:hover': { color: '#2563eb' } }}
                    />
                  </Box>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Selected Indicators */}
      {formData.indicators.length === 0 ? (
        <Box
          sx={{
            border: '2px dashed rgba(255,255,255,0.2)',
            borderRadius: '8px',
            p: 4,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.5)'
          }}
        >
          <Typography>No indicators selected. Add an indicator above.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {formData.indicators.map(indicator => (
            <Card
              key={indicator.componentId}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px'
              }}
            >
              <CardContent>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Indicator"
                      size="small"
                      sx={{ backgroundColor: '#2563eb', color: '#fff' }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#fff' }}>
                      {indicator.component.name}
                    </Typography>
                    {indicator.component.tooltip && (
                      <InfoPopover
                        title={indicator.component.name}
                        description={indicator.component.tooltip}
                        tags={indicator.component.tags}
                        accentColor="#2563eb"
                      />
                    )}
                  </Box>
                  <IconButton
                    onClick={() => handleRemoveIndicator(indicator.componentId)}
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ef4444' } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                {/* Parameters */}
                <Grid container spacing={2}>
                  {indicator.parameters.map(param => (
                    <Grid item xs={12} sm={6} key={param.name}>
                      <ParamInput
                        param={param}
                        value={indicator.values[param.name] ?? param.defaultValue ?? ''}
                        onChange={(value) => handleParamChange(indicator.componentId, param.name, value)}
                        accentColor="#2563eb"
                        symbolOptions={formData.selectedSymbols}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default IndicatorStep;
